import { GraphClient } from '@micro-frontend-react/employee-experience/lib/GraphClient';
import { HttpClient } from '@micro-frontend-react/employee-experience/lib/HttpClient';
import { ITelemetryContext } from '@micro-frontend-react/employee-experience/lib/ITelemetryContext';
import { ReducerRegistry } from '@micro-frontend-react/employee-experience/lib/ReducerRegistry';
import { Shell } from '@micro-frontend-react/employee-experience/lib/Shell';
import { StoreBuilder } from '@micro-frontend-react/employee-experience/lib/StoreBuilder';
import { TelemetryClient } from '@micro-frontend-react/employee-experience/lib/TelemetryClient';
import { UsageHelper, UserAttribute } from '@micro-frontend-react/employee-experience/lib/UsageTelemetry';
import { withStore } from '@micro-frontend-react/employee-experience/lib/withStore';
import { AuthClient } from '@micro-frontend-react/employee-experience/lib/AuthClient';
import { IHttpClientOption } from '@micro-frontend-react/employee-experience/lib/IHttpClient';
import { CorrelationVector } from 'mscv';
import axios from 'axios';

const appName = 'SkillCentral';

// MS-CV interceptor on global axios
axios.interceptors.request.use((config) => {
    const newConfig = { ...config };
    const mscv = CorrelationVector.createCorrelationVector(1).value;
    newConfig.headers['MS-CV'] = mscv;
    return newConfig;
});

// eslint-disable-next-line react-refresh/only-export-components
export const telemetryClient = new TelemetryClient({
    instrumentationKey: import.meta.env.__INSTRUMENTATION_KEY__,
    UTPConfig: {
        EnvironmentName: import.meta.env.__ENVIRONMENT_NAME__,
        ServiceOffering: import.meta.env.__SERVICE_OFFERING__,
        ServiceLine: import.meta.env.__SERVICE_LINE__,
        Service: import.meta.env.__SERVICE__,
        ComponentName: import.meta.env.__COMPONENT_NAME__,
        ComponentId: import.meta.env.__COMPONENT_ID__,
    },
    defaultProperties: { appName },
});

const usageHelper = UsageHelper.Fork(appName);

const telemetryContext: ITelemetryContext = {
    sourceComponent: 'Shell',
    sourceScript: 'main',
    setUsageEvent: usageHelper.MassageEvent,
    setUsageUser: (usageUser: UserAttribute) => {
        UsageHelper.SetUser(usageUser);
        usageHelper.SetUser(usageUser);
        return usageUser;
    },
    usageUser: usageHelper.GetUser,
    setUsageConfig: usageHelper.SetUsageConfig,
    getChildContext: usageHelper.ForkTelemetryContext,
};

telemetryClient.setContext(telemetryContext);

// During live integration tests (itest:auth / itest:live) MSAL must persist tokens
// in localStorage so Playwright's storageState can capture and reuse the signed-in
// session across runs. sessionStorage (the default) is not captured by storageState.
// __ITEST_LIVE__ is a build-time literal injected by Vite `define` (see vite.config.ts):
// it is `false` in every non-live build, so the localStorage branch below is
// dead-code-eliminated from dev/prod bundles, which keep the XSS-resistant
// sessionStorage default.
declare const __ITEST_LIVE__: boolean;
const isLiveIntegrationTest = __ITEST_LIVE__;

// eslint-disable-next-line react-refresh/only-export-components
export const authClient = new AuthClient(
    {
        auth: {
            clientId: import.meta.env.__CLIENT_ID__,
            redirectUri: window.location.origin,
            authority: 'https://login.microsoftonline.com/microsoft.onmicrosoft.com',
        },
        ...(isLiveIntegrationTest ? { cache: { cacheLocation: 'localStorage' } } : {}),
    },
    telemetryClient
);

const httpClientOptions: IHttpClientOption = { correlationIdHeaderName: 'MS-CV' };
const httpClient = new HttpClient(telemetryClient, authClient, httpClientOptions);
const graphClient = new GraphClient(httpClient);
const reducerRegistry = new ReducerRegistry();
const storeResult = new StoreBuilder(reducerRegistry, {})
    .configureSaga({ telemetryClient, authClient, httpClient, graphClient, appName })
    .configurePersistor({ blacklist: ['dynamic'] })
    .addMiddleware(() => (next) => (action) => {
        if (action.type !== 'persist/REHYDRATE' && action.type !== 'persist/PERSIST') {
            next(action);
        }
    })
    .build();

export const ShellWithStore = withStore(storeResult)(Shell) as React.ComponentType<React.PropsWithChildren>;
